import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "axios";
import ReactLoading from "react-loading";
import {Bounce, toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./output.css";
import _ from "lodash";

function App() {
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormValid, setIsFormValid] = useState(true);
  const [darkMode] = useState(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("theme") !== "light";
  });
  const iframeRef = React.useRef(null);

  const notify = useCallback(() =>
    toast.error(
      'Invalid card details. Please try again.',
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: darkMode ? "dark" : "light",
        transition: Bounce,
      }
    ), [darkMode]);

  const throttleNotify = useMemo(
    () => _.throttle(notify, 1000, {trailing: false}),
    [notify]
  );

  useEffect(() => {
    const handleMessage = async (event) => {
      // Log all messages for debugging
      console.log('Message received:', {
        origin: event.origin,
        data: event.data,
        eventType: event.data?.eventType,
      });

      // Validate origin for security
      const allowedOrigins = [
        window.location.origin,
        'https://widgets.mindbodyonline.com',
        'https://mindbodyonline.com'
      ];
      const isAllowedOrigin = allowedOrigins.some(origin =>
        event.origin === origin || event.origin.endsWith('.mindbodyonline.com')
      );
      if (!isAllowedOrigin) {
        return;
      }

      // Forward all messages to parent window (only if embedded)
      if (window.parent !== window) {
        window.parent.postMessage(event.data, '*');
      }

      // Handle different event types
      switch (event.data?.eventType) {
        case 'onValidationSucceed':
          console.log('Form validation passed');
          setIsFormValid(true);
          break;

        case 'onValidationFailed':
          console.log('Form validation failed');
          setIsFormValid(false);
          break;

        case 'onFinalizeSucceed':
          console.log('Payment success:', event.data);
          break;

        case 'onFinalizeFailed':
          console.log('Payment failed:', event.data);
          setIsFormValid(true);
          throttleNotify();
          break;

        default:
          console.log('Event:', event.data?.eventType, event.data);
      }
    };

    window.addEventListener("message", handleMessage, false);

    fetchIframeUrl();

    return () => {
      window.removeEventListener("message", handleMessage);
      throttleNotify.cancel();
    };
  }, [throttleNotify]);

  const fetchIframeUrl = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const studioId = queryParams.get("studio_id");
    const baseUrl = process.env.REACT_APP_MEMBER_API_BASE_URL;

    if (!studioId) {
      setError("No studio_id found in URL. Please provide a valid studio_id parameter.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${baseUrl}/member/studios/${studioId}/payments/credit-card-url`,
        {
          callbackUrl: window.location.origin,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setIframeUrl(response.data.data.paymentUrl);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching iframe URL:", err);
      setError(err.response?.data?.message || "Failed to load payment form. Please try again later.");
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: darkMode ? '#1A1A1A' : '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      <div
        className="App"
        style={{
          maxWidth: '650px',
          margin: '0 auto',
        }}
      >
      <ToastContainer theme={darkMode ? "dark" : "light"}/>

      {error && (
        <div style={{
          color: darkMode ? '#9CA3AF' : '#616161',
          height: '450px',
          paddingTop: '200px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {!error && (
        <>
          {loading && (
            <div className="flex justify-center items-center" style={{height: '400px'}}>
              <ReactLoading
                type={"spin"}
                color={"#847A59"}
                height={"50px"}
                width={"50px"}
              />
            </div>
          )}

          {iframeUrl && (
            <iframe
              allow="clipboard-read *; clipboard-write *"
              ref={iframeRef}
              src={iframeUrl}
              style={{
                top: 0,
                left: 0,
                border: "none",
                height: "400px",
              }}
              title="Embedded Iframe"
              onLoad={() => setLoading(false)}
              className="w-full h-1/2"
            />
          )}

          {!loading && iframeUrl && (
            <div
              className="flex justify-center items-center px-6"
              style={{height: "50px"}}
            >
              <button
                className="py-2 px-8 w-full submit-button"
                style={{
                  height: "52px",
                  fontSize: "14px",
                  fontWeight: "500",
                  backgroundColor: isFormValid ? "#847A59" : "#A0A0A0",
                  color: "white",
                  opacity: isFormValid ? 1 : 0.6,
                  transition: "all 0.3s ease",
                  border: "1px solid transparent",
                }}
                disabled={!isFormValid}
                onMouseEnter={(e) => {
                  if (isFormValid) {
                    e.target.style.opacity = "0.8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isFormValid) {
                    e.target.style.opacity = "1";
                  }
                }}
                onClick={() => {
                  setIsFormValid(false);
                  if (!iframeRef.current?.contentWindow) {
                    console.error('Iframe not ready');
                    return;
                  }
                  console.log('Sending onFinalize to iframe...');
                  iframeRef.current.contentWindow.postMessage(
                    {eventType: "onFinalize"},
                    "*"
                  );
                  // Send event for mobile app WebView (React Native)
                  if (window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({eventType: "onFinalize"}));
                  }
                }}
              >
                SUBMIT
              </button>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

export default App;
